import { useMemo } from 'react';
import { __AUTHORIZATIONS__ } from 'shell/constants/environment';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { AuthorizationItems } from 'shell/models/authorizations';

type ActionsInput<T> = {
  readonly [K in keyof T]: AuthorizationItems;
};

type ActionsActive<T> = {
  readonly [K in keyof T]: boolean;
};

/**
 * The hook to get check if the user is authorized to do some actions or not
 * @param {ActionsInput<T>} actions the actions
 * @returns {ActionsActive<T>} is authorized or not for the action
 */
export const useActionAuthorization = <T>(actions: ActionsInput<T>): ActionsActive<T> => {
  const { userProfile } = useUserProfile();

  const authorizationItems = useMemo(
    () => userProfile.datiLogin?.profile?.authorizationItems?.slice() || [],
    [userProfile.datiLogin?.profile?.authorizationItems]
  );

  const actionsActive = useMemo(() => {
    const actionsActiveReturn: Record<keyof T, boolean> | Partial<Record<keyof T, boolean>> = {};

    const actionsToCheck = { ...actions } as unknown as Record<string, AuthorizationItems>;

    (Object.keys(actionsToCheck) as Array<keyof T>).forEach((actionKey) => {
      const foundAuth = authorizationItems.find((authorization: string) => {
        return actions[actionKey].find((action: string) => action === authorization);
      });

      actionsActiveReturn[actionKey] = __AUTHORIZATIONS__ ? !!foundAuth : true;
    });

    return actionsActiveReturn as ActionsActive<T>;
  }, [authorizationItems, actions]);

  return actionsActive;
};
